import { Stack, Typography } from '@mui/material';
import { ICaseDetailsInputs } from 'API';
import {
  ControllerAutoComplete,
  CustomCheckbox,
  CustomRadioGroup,
  FileUpload,
  RadioButtons,
} from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY, FileCategories } from 'Enum';
import { useAppSelector, useDictionary } from 'Hooks';
import { IndexContext } from 'Modules/CaseDetails/CaseContext';
import { useContext, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { isFileExist } from 'Utils/isFileExist';

export const IsThereInjuryReport = () => {
  const { index, fieldId } = useContext(IndexContext);
  const { clearErrors, setValue } = useFormContext<ICaseDetailsInputs>();

  const [reportAttachedOptions, investigationDirectorOptions] = useAppSelector(
    ({
      generalContent: {
        generalContent: { reportAttachedOptions, investigationDirectorOptions },
      },
    }) => [reportAttachedOptions, investigationDirectorOptions]
  );

  const filesList = useAppSelector(({ file: { files } }) => files);

  const baseName = `caseDetails.${index}.injuryKindSection`;

  const reportOptionId = useWatch({
    name: `${baseName}.reportAttachedSelectedOptionId`,
  }) as number;
  const [
    isInvestigationHasBeen,
    hasNoInvestigator,
    hasNoInvestigationReportFile,
    isInjuryReportFileClassified,
  ] = useWatch({
    name: [
      `${baseName}.investigationHasBeen`,
      `${baseName}.hasNoInvestigator`,
      `${baseName}.hasNoInvestigationReportFile`,
      `${baseName}.isInjuryReportFileClassified`,
    ],
  });

  const dictionary = useDictionary(Dictionary, DICTIONARY.CASE_DETAILS);
  const HAS_REPORT_FILE = 1;

  useEffect(() => {
    if (hasNoInvestigator) {
      setValue(
        `${baseName}.investigatorOptionId` as 'caseDetails.0.injuryKindSection.investigatorOptionId',
        null
      );
      clearErrors(
        `${baseName}.investigatorOptionId` as 'caseDetails.0.injuryKindSection.investigatorOptionId'
      );
    }
  }, [clearErrors, setValue, baseName, hasNoInvestigator]);

  return (
    <>
      <CustomRadioGroup
        id={dictionary.secondSectionReportTitle}
        removeBold
        name={`${baseName}.reportAttachedSelectedOptionId` as const}
        options={reportAttachedOptions.map(({ id, value }) => ({
          value: id.toString(),
          label: value,
        }))}
      />
      {reportOptionId == HAS_REPORT_FILE && (
        <Stack gap='10px' sx={{ mt: '10px' }}>
          <Typography>{dictionary.secondSectionReportFile}</Typography>
          <Stack>
            <FileUpload
              caseDetailsId={fieldId}
              fileLabel={dictionary.secondSectionFileAria}
              fileCategoryId={FileCategories.InjuryReportFiLe}
              ariaLabel={dictionary.secondSectionFileAria}
              disabled={isInjuryReportFileClassified}
            />
            {!isFileExist(filesList, FileCategories.InjuryReportFiLe) && (
              <CustomCheckbox
                name={
                  `${baseName}.isInjuryReportFileClassified` as `caseDetails.0.injuryKindSection.isInjuryReportFileClassified`
                }
                label={dictionary.classifiedInformationReportLater}
              />
            )}
          </Stack>
        </Stack>
      )}
      <RadioButtons
        id={dictionary.secondSectionArmyReportTitle}
        name={`${baseName}.investigationHasBeen`}
        options={[
          {
            value: 'true',
            label: dictionary.secondSectionFamilyInjuryYes,
          },
          {
            value: 'false',
            label: dictionary.secondSectionFamilyInjuryNo,
          },
        ]}
      />
      {isInvestigationHasBeen && (
        <>
          <br />
          <Stack>
            <ControllerAutoComplete
              options={investigationDirectorOptions}
              name={`${baseName}.investigatorOptionId` as const}
              label={dictionary.secondSectionReportInvestigator}
              disabled={hasNoInvestigator}
            />
            <CustomCheckbox
              name={
                `${baseName}.hasNoInvestigator` as `caseDetails.0.injuryKindSection.hasNoInvestigator`
              }
              label={dictionary.noInformationOrRemember}
            />
          </Stack>
          <Stack>
            <Typography sx={{ mb: '10px' }}>
              {dictionary.secondSectionReportFileTitle}
            </Typography>
            <Stack>
              <FileUpload
                caseDetailsId={fieldId}
                fileLabel={dictionary.secondSectionFileAria}
                fileCategoryId={FileCategories.InvestigationReportFile}
                ariaLabel={dictionary.secondSectionFileAria}
                disabled={hasNoInvestigationReportFile}
                dataRequired={!hasNoInvestigationReportFile}
              />
              {!isFileExist(
                filesList,
                FileCategories.InvestigationReportFile
              ) && (
                <CustomCheckbox
                  name={
                    `${baseName}.hasNoInvestigationReportFile` as `caseDetails.0.injuryKindSection.hasNoInvestigationReportFile`
                  }
                  label={dictionary.noInformationOrRemember}
                />
              )}
            </Stack>
          </Stack>
          <Stack>
            <Typography sx={{ mb: '10px' }}>
              {dictionary.secondSectionReportFileTitle2}
            </Typography>
          </Stack>
        </>
      )}
    </>
  );
};
